import classNames from 'classnames';
import style from './toggle.module.scss';

export type ToggleSize = 'small' | 'medium' | 'large';

export interface ToggleProps {
  /** Whether the toggle is active or not */
  isActive: boolean;
  /** Callback when toggle state changes */
  handleToggle: (isActive: boolean) => void;
  /** Size of the toggle */
  size?: ToggleSize;
  /** Optional label text to display next to toggle */
  label?: string;
  /** Position of the label relative to toggle */
  labelPosition?: 'left' | 'right';
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Aria label for accessibility */
  ariaLabel?: string;
}

export const Toggle = ({
  isActive,
  handleToggle,
  size = 'small',
  label,
  labelPosition = 'right',
  disabled = false,
  className,
  ariaLabel,
}: ToggleProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      handleToggle(e.target.checked);
    }
  };

  const sizeClass =
    size === 'small'
      ? style.toggleSmall
      : size === 'medium'
        ? style.toggleMedium
        : style.toggleLarge;

  const toggleClasses = classNames(style.toggle, sizeClass, {
    [style.toggleOn]: isActive,
    [style.toggleOff]: !isActive,
    [style.toggleDisabled]: disabled,
  });

  const containerClasses = classNames(
    style.container,
    {
      [style.containerLabelLeft]: label && labelPosition === 'left',
      [style.containerLabelRight]: label && labelPosition === 'right',
      [style.containerDisabled]: disabled,
    },
    className
  );

  const toggleElement = (
    <label className={toggleClasses}>
      <input
        type="checkbox"
        checked={isActive}
        onChange={handleChange}
        disabled={disabled}
        aria-label={ariaLabel || label || (isActive ? 'On' : 'Off')}
        className={style.toggleInput}
      />
      <span className={style.toggleTrack}>
        <span className={style.toggleThumb} />
      </span>
    </label>
  );

  if (!label) {
    return toggleElement;
  }

  return (
    <div className={containerClasses}>
      {labelPosition === 'left' && <span className={style.label}>{label}</span>}
      {toggleElement}
      {labelPosition === 'right' && (
        <span className={style.label}>{label}</span>
      )}
    </div>
  );
};
